import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getDoctorSlots } from "../api/doctors.api";
import { getFamilyMembers } from "../api/family.api";
import { getWallet } from "../api/staff.api";
import { getAssetUrl } from "../api/config";
import { getStaffId, getStaffName } from "../utils/storage";
import { getSpendableWalletBalance, formatWalletAmount } from "../utils/wallet";
import Header from "../Components/Header";
import LoadingState from "../Components/States/LoadingState";
import Footer from "../Components/Footer";
import FamilyMemberModal from "../Components/FamilyMemberModal";
import { FaArrowLeft, FaCalendarAlt, FaClock, FaUser, FaUsers, FaMapMarkerAlt } from "react-icons/fa";

const toYMD = (d) => {
  const x = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(x.getTime())) return String(d).slice(0, 10);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
};

const doctorName = (doctor) => {
  const n = doctor?.name || "Doctor";
  return /^dr\.?\s/i.test(n) ? n : `Dr. ${n}`;
};

const isMorning = (timeSlot) => {
  const t = String(timeSlot || "");
  if (/am/i.test(t)) return true;
  if (/pm/i.test(t)) return false;
  return parseInt(t, 10) < 12;
};

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

const ScheduleAppointmentPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const doctor = location.state?.doctor;
  const staffId = getStaffId();
  const staffName = getStaffName() || "Myself";

  const [consultationType, setConsultationType] = useState(location.state?.consultationType || "Online");
  const [monthCursor, setMonthCursor] = useState(() => new Date());
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [patient, setPatient] = useState({ type: "self", name: staffName, familyMemberId: null });
  const [familyModalOpen, setFamilyModalOpen] = useState(false);
  const [walletData, setWalletData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!doctor) navigate("/consultations", { replace: true });
  }, [doctor, navigate]);

  useEffect(() => {
    if (!staffId) return;
    getFamilyMembers(staffId)
      .then((res) => {
        const members = res.data?.family_members || [];
        setFamilyMembers(members);
        const selfMember = members.find((m) => /self/i.test(m.relation || ""));
        if (selfMember) {
          setPatient({ type: "self", name: staffName, familyMemberId: selfMember._id });
        }
      })
      .catch(() => {});
    getWallet(staffId)
      .then((res) => setWalletData(res.data))
      .catch(() => {});
  }, [staffId, staffName]);

  const doctorId = doctor?._id || doctor?.id;

  const fetchSlots = (date) => {
    if (!doctorId || !date) return;
    setLoadingSlots(true);
    setSelectedSlot(null);
    getDoctorSlots(doctorId, date, consultationType)
      .then((res) => {
        const slots = res.data?.slots || [];
        setAvailableSlots([...new Map(slots.map((s) => [s.timeSlot, s])).values()]);
      })
      .catch(() => setAvailableSlots([]))
      .finally(() => setLoadingSlots(false));
  };

  const handleDateSelect = (ymd) => {
    setSelectedDate(ymd);
    fetchSlots(ymd);
  };

  const handleTypeChange = (type) => {
    setConsultationType(type);
    setSelectedDate("");
    setSelectedSlot(null);
    setAvailableSlots([]);
  };

  const handleFamilySaved = (member) => {
    setFamilyMembers((prev) => [...prev, member]);
    setPatient({ type: "family", name: member.fullName, familyMemberId: member._id });
    setError("");
  };

  const handleConfirm = () => {
    if (!selectedDate || !selectedSlot) {
      setError("Select a date and time slot.");
      return;
    }
    if (!patient) {
      setError("Select who this appointment is for.");
      return;
    }
    navigate("/consultations/review", {
      state: {
        doctor,
        consultationType,
        selectedDate,
        selectedSlot,
        patient,
        walletData,
      },
    });
  };

  if (!doctor) return null;

  const year = monthCursor.getFullYear();
  const month = monthCursor.getMonth();
  const firstWeekday = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const todayYmd = toYMD(new Date());
  const latest = new Date();
  latest.setDate(latest.getDate() + 30);
  const latestYmd = toYMD(latest);
  const startMonth = new Date().getMonth();
  const startYear = new Date().getFullYear();
  const canPrevMonth = year > startYear || month > startMonth;
  const canNextMonth = year < latest.getFullYear() || month < latest.getMonth();
  const cells = [
    ...Array.from({ length: firstWeekday }, () => null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];

  const morning = availableSlots.filter((s) => !s.isExpired && isMorning(s.timeSlot));
  const afternoon = availableSlots.filter((s) => !s.isExpired && !isMorning(s.timeSlot));
  const fee = Number(doctor.consultation_fee) || 0;
  const walletBal = getSpendableWalletBalance(walletData);
  const walletUse = Math.min(fee, walletBal);
  const formattedWhen = selectedDate && selectedSlot
    ? `${new Date(selectedDate + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" })}, ${selectedSlot.timeSlot}`
    : "Select date & time";

  return (
    <div className="bg-surface-low min-h-screen flex flex-col">
      <Header />
      <div className="flex flex-col min-h-screen pb-16 lg:pb-0 flex-1">
        <main className="flex-1 max-w-7xl mx-auto w-full px-3 sm:px-6 py-4 sm:py-6">
          <div className="flex items-center gap-3 mb-1">
            <button
              onClick={() => navigate("/consultations")}
              className="text-primary p-1"
              aria-label="Back"
            >
              <FaArrowLeft size={16} />
            </button>
            <h1 className="text-2xl sm:text-3xl font-bold text-primary">Schedule Appointment</h1>
          </div>
          <p className="text-on-surface-variant mb-6 ml-8">Select a date, time, and patient for your consultation.</p>

          {error && <p className="text-error text-sm mb-4">{error}</p>}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-surface-lowest rounded-xl border border-surface-container p-5 shadow-sm">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <div className="flex items-center gap-2 font-semibold text-on-primary-fixed-var mb-3">
                      <FaCalendarAlt className="text-primary" /> Date
                    </div>
                    <div className="flex items-center justify-between mb-2">
                      <button
                        type="button"
                        disabled={!canPrevMonth}
                        onClick={() => setMonthCursor(new Date(year, month - 1, 1))}
                        className="px-2 py-1 text-sm text-primary disabled:opacity-30"
                      >
                        Prev
                      </button>
                      <span className="font-semibold text-on-primary-fixed-var">
                        {MONTHS[month]} {year}
                      </span>
                      <button
                        type="button"
                        disabled={!canNextMonth}
                        onClick={() => setMonthCursor(new Date(year, month + 1, 1))}
                        className="px-2 py-1 text-sm text-primary disabled:opacity-30"
                      >
                        Next
                      </button>
                    </div>
                    <div className="grid grid-cols-7 text-center text-xs text-on-surface-variant mb-1">
                      {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
                        <span key={`${d}-${i}`}>{d}</span>
                      ))}
                    </div>
                    <div className="grid grid-cols-7 gap-1">
                      {cells.map((day, i) => {
                        if (!day) return <span key={`e-${i}`} />;
                        const ymd = toYMD(new Date(year, month, day));
                        const enabled = ymd >= todayYmd && ymd <= latestYmd;
                        const selected = selectedDate === ymd;
                        return (
                          <button
                            key={ymd}
                            type="button"
                            disabled={!enabled}
                            onClick={() => handleDateSelect(ymd)}
                            className={`h-9 w-9 mx-auto rounded-full text-sm ${
                              selected
                                ? "bg-primary text-white"
                                : enabled
                                  ? "hover:bg-primary-fixed/50 text-on-primary-fixed-var"
                                  : "text-on-surface-variant/40 cursor-not-allowed"
                            }`}
                          >
                            {day}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center gap-2 font-semibold text-on-primary-fixed-var mb-3">
                      <FaClock className="text-primary" /> Time
                    </div>
                    {!selectedDate ? (
                      <p className="text-sm text-on-surface-variant">Pick a date to see times.</p>
                    ) : loadingSlots ? (
                      <LoadingState size="sm" message="Loading slots…" className="py-4" />
                    ) : (
                      <>
                        <p className="text-xs font-semibold text-on-surface-variant mb-2">Morning</p>
                        <div className="flex flex-wrap gap-1.5 mb-4">
                          {morning.length === 0 && <span className="text-xs text-on-surface-variant">None</span>}
                          {morning.map((slot) => (
                            <button
                              key={slot._id || slot.timeSlot}
                              onClick={() => setSelectedSlot(slot)}
                              className={`px-2.5 sm:px-3 py-1.5 rounded-lg border text-xs sm:text-sm ${
                                selectedSlot?.timeSlot === slot.timeSlot
                                  ? "border-primary bg-primary-fixed/50 text-on-primary-fixed-var"
                                  : "border-surface-container bg-surface-lowest"
                              }`}
                            >
                              {slot.timeSlot}
                            </button>
                          ))}
                        </div>
                        <p className="text-xs font-semibold text-on-surface-variant mb-2">Afternoon</p>
                        <div className="flex flex-wrap gap-1.5">
                          {afternoon.length === 0 && <span className="text-xs text-on-surface-variant">None</span>}
                          {afternoon.map((slot) => (
                            <button
                              key={slot._id || slot.timeSlot}
                              onClick={() => setSelectedSlot(slot)}
                              className={`px-2.5 sm:px-3 py-1.5 rounded-lg border text-xs sm:text-sm ${
                                selectedSlot?.timeSlot === slot.timeSlot
                                  ? "border-primary bg-primary-fixed/50 text-on-primary-fixed-var"
                                  : "border-surface-container bg-surface-lowest"
                              }`}
                            >
                              {slot.timeSlot}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div className="bg-surface-lowest rounded-xl border border-surface-container p-4 sm:p-5 shadow-sm">
                <div className="flex items-center gap-2 font-semibold text-on-primary-fixed-var mb-4">
                  <FaUser className="text-primary" /> Who is this appointment for?
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      const selfMember = familyMembers.find((m) => /self/i.test(m.relation || ""));
                      setPatient({ type: "self", name: staffName, familyMemberId: selfMember?._id || null });
                    }}
                    className={`p-3 sm:p-4 rounded-xl border text-left min-w-0 ${
                      patient.type === "self"
                        ? "border-primary bg-primary-fixed/40"
                        : "border-surface-container"
                    }`}
                  >
                    <FaUser className="text-primary mb-2" />
                    <p className="font-semibold text-sm text-on-primary-fixed-var">Myself</p>
                    <p className="text-xs text-on-surface-variant truncate">{staffName}</p>
                  </button>

                  {familyMembers.filter((m) => !/self/i.test(m.relation || "")).map((member) => (
                    <button
                      key={member._id}
                      type="button"
                      onClick={() => setPatient({ type: "family", name: member.fullName, familyMemberId: member._id })}
                      className={`p-3 sm:p-4 rounded-xl border text-left min-w-0 ${
                        patient.familyMemberId === member._id
                          ? "border-primary bg-primary-fixed/40"
                          : "border-surface-container"
                      }`}
                    >
                      <FaUsers className="text-primary mb-2" />
                      <p className="font-semibold text-sm text-on-primary-fixed-var">Family Member</p>
                      <p className="text-xs text-on-surface-variant truncate">{member.fullName}</p>
                    </button>
                  ))}

                  <button
                    type="button"
                    onClick={() => setFamilyModalOpen(true)}
                    className="p-3 sm:p-4 rounded-xl border border-dashed border-surface-variant text-left min-w-0"
                  >
                    <FaUsers className="text-on-surface-variant mb-2" />
                    <p className="font-semibold text-sm text-on-primary-fixed-var">Add Family Member</p>
                    <p className="text-xs text-on-surface-variant">New profile</p>
                  </button>
                </div>
              </div>
            </div>

            <aside className="lg:sticky lg:top-20 h-fit bg-surface-lowest rounded-xl border border-surface-container p-5 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <img
                  src={getAssetUrl(doctor.image) || "/logo.png"}
                  alt={doctorName(doctor)}
                  className="w-14 h-14 rounded-full object-cover"
                  onError={(e) => { e.target.src = "/logo.png"; }}
                />
                <div>
                  <p className="font-bold text-on-primary-fixed-var">{doctorName(doctor)}</p>
                  <p className="text-sm text-on-surface-variant">{doctor.specialization || "General Physician"}</p>
                </div>
              </div>
              {doctor.address && (
                <p className="text-sm text-on-surface-variant flex items-start gap-2 mb-4">
                  <FaMapMarkerAlt className="mt-0.5 shrink-0 text-primary" />
                  {doctor.address}
                </p>
              )}
              <dl className="text-sm space-y-2 mb-4">
                <div className="flex justify-between gap-2">
                  <dt className="text-on-surface-variant">Date & Time</dt>
                  <dd className="text-right font-medium">{formattedWhen}</dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt className="text-on-surface-variant">Patient</dt>
                  <dd className="text-right font-medium">{patient.name}{patient.type === "self" ? " (Self)" : ""}</dd>
                </div>
                <div>
                  <div className="flex justify-between gap-2">
                    <dt className="text-on-surface-variant">Type</dt>
                    <dd className="font-medium">{consultationType === "Online" ? "Video Consultation" : "In-Person"}</dd>
                  </div>
                  {/* <button
                    type="button"
                    className="text-xs text-primary underline mt-1"
                    onClick={() => handleTypeChange(consultationType === "Online" ? "Offline" : "Online")}
                  >
                    Change to {consultationType === "Online" ? "In-person" : "Video"}
                  </button> */}
                </div>
              </dl>
              <div className="bg-surface-low rounded-lg p-3 text-sm mb-4 space-y-1">
                <div className="flex justify-between">
                  <span>Consultation Fee</span>
                  <span>₹{fee.toLocaleString("en-IN")}</span>
                </div>
                <div className="flex justify-between text-primary">
                  <span>Wallet Deduction</span>
                  <span>-₹{walletUse.toLocaleString("en-IN")}</span>
                </div>
                <p className="text-xs text-on-surface-variant pt-1">
                  Available corporate balance: ₹{formatWalletAmount(walletBal)}
                </p>
              </div>
              <button
                onClick={handleConfirm}
                disabled={!selectedDate || !selectedSlot}
                className="w-full py-3 rounded-lg bg-primary text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Confirm Appointment →
              </button>
              <p className="text-xs text-center text-on-surface-variant mt-2">You won&apos;t be charged yet.</p>
            </aside>
          </div>
        </main>
        <Footer />
      </div>

      <FamilyMemberModal
        open={familyModalOpen}
        onClose={() => setFamilyModalOpen(false)}
        mode="create"
        member={null}
        isPrimary={false}
        staffId={staffId}
        onSaved={handleFamilySaved}
      />
    </div>
  );
};

export default ScheduleAppointmentPage;
